import { useTranslation } from 'react-i18next';
import { LoaderFunctionArgs, redirect, useNavigate, useParams } from 'react-router-dom';
import isEqual from 'lodash/isEqual';

import { useOrderData } from 'hooks/orderForms/useOrderData';
import { logError } from 'services/logging/logging';
import { useOrderForm } from 'services/repositories/orderForms/orderForms';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { getOrderFormSessionKey, OrderData } from 'support/helpers/orderForms/orderForms';
import { RouteParams } from 'support/helpers/types/types';
import { OrderFormsRoutes, TransactionInternalType } from 'support/types';

import { OrderFormPageLayout } from 'components/OrderFormPageLayout/OrderFormPageLayout';

import { useCreateOrder } from './hooks/useCreateOrder';

import { BackButton } from './components/BackButton/BackButton';
import { CheckoutForm } from './components/CheckoutForm/CheckoutForm';
import { OrderBreakdown } from './components/OrderBreakdown/OrderBreakdown';

const OrderFormCheckout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { supplierSlug } = useParams<RouteParams<OrderFormsRoutes.checkout>>();
  const [orderData] = useOrderData(supplierSlug || '');

  const { data: orderFormData } = useOrderForm({
    variables: {
      supplierSlugName: supplierSlug || '',
    },
  });

  const { createOrder, isLoading: isSubmitting } = useCreateOrder(orderFormData);

  const onSubmit = async () => {
    try {
      if (!orderData.parties || isEqual(orderData.parties, {})) {
        logError('Order Forms: No parties data available');
        addNotification(t('common:error.generic'), NotificationType.error);
        return;
      }
      const response = await createOrder(orderData);
      if (response?.transactionType === TransactionInternalType.message) {
        navigate(
          routeToPage(OrderFormsRoutes.successMessage, { supplierSlug: supplierSlug!, id: response.transactionId }),
        );
      } else {
        navigate(
          routeToPage(OrderFormsRoutes.successSenderEnvelope, {
            supplierSlug: supplierSlug!,
            id: response.transactionId,
          }),
        );
      }
    } catch (error) {
      logError(error);
      addNotification(t('common:error.generic'), NotificationType.error);
    }
  };

  return (
    <OrderFormPageLayout>
      <div className="flex gap-12">
        <div className="flex-1">
          {supplierSlug ? (
            <div className="space-y-3">
              <BackButton supplierSlug={supplierSlug} disabled={isSubmitting} />
              <CheckoutForm supplierSlug={supplierSlug} onSubmit={onSubmit} isSubmitting={isSubmitting} />
            </div>
          ) : null}
        </div>
        <div className="w-[480px]">
          <OrderBreakdown />
        </div>
      </div>
    </OrderFormPageLayout>
  );
};

OrderFormCheckout.loader = async (args: LoaderFunctionArgs) => {
  const params = args.params as RouteParams<OrderFormsRoutes.checkout>;
  const sessionStorageKey = getOrderFormSessionKey(params.supplierSlug);
  const orderData = sessionStorage.getItem(sessionStorageKey);

  const redirectToRoot = () => redirect(routeToPage(OrderFormsRoutes.root, { supplierSlug: params.supplierSlug }));

  if (!orderData) {
    return redirectToRoot();
  }

  try {
    const parsedOrderData: OrderData = JSON.parse(orderData);
    const hasOrderedProducts =
      parsedOrderData.products && Object.values(parsedOrderData.products).some((product) => product.quantity > 0);

    if (!hasOrderedProducts) {
      return redirectToRoot();
    }
  } catch (error) {
    logError(error);
    return redirectToRoot();
  }

  return null;
};

export { OrderFormCheckout };
